import React, { useState, useCallback } from 'react';
import { Link, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import CardList from '../components/cards/card-list';
import { useChangePermissionsMutation, useGetGroupAppsQuery } from '../services/groups.service';
import { AppsBehavior } from '../models/group';
import type { GroupsForm } from '../models/group';
import ConfirmModal from '../components/modals/confirm-modal';
import Search from '../components/search';
import linkTo from '../utils/linkTo';
import { searchSortHeaderStyles } from './__fixtures__';
import { listResourcesFallbackUIProps } from './group-fallback-ui';
import { OptionItem, EventData } from '../components/cards/card.types';

export interface Props {
  groupId: string;
}

const appsBehavior = new AppsBehavior();

export default function GroupsApps({ groupId }: Props) {
  const [params, setParams] = useState({
    id: groupId,
    page: 1,
    per_page: 100,
    sort: 'name',
    search: '',
  });

  const { data: group, isFetching, isError } = useGetGroupAppsQuery({ ...params });
  const [appId, setAppId] = useState('');
  const [apps, setApps] = useState<GroupsForm[]>([]);
  const [changePermission] = useChangePermissionsMutation();
  const groupApps = React.useMemo(
    () => [...apps, ...group?.forms ?? []], [group?.forms],
  );
  const showSearch = !!groupApps.length || (!groupApps.length && !!params.search.length);
  const isSearching = params.search !== '';
  const [removedAppName, setRemovedAppName] = useState<string | undefined>('');

  const handleRemove = () => {
    changePermission({
      type: 'group_forms',
      groupId,
      remove: [appId],
      item: removedAppName,
    });
    resetDeleteModal();
  };

  const handleFetchMore = useCallback(() => {
    if (group && params.page !== group.total_pages) {
      setParams({ ...params, page: params.page + 1 });
      setApps(groupApps);
    }
  }, [groupApps]);

  const handleSearch = (search: string) => {
    setApps([]);
    setParams({ ...params, page: 1, search });
  };
  const removeClick = (id: string, title: string) => {
    setAppId(id);
    setRemovedAppName(title);
  };
  const resetDeleteModal = () => setAppId('');

  const { EmptyImage, emptyText, emptyTextHelper } = listResourcesFallbackUIProps({
    resource: 'apps',
    isSearching,
  });

  const onRemoveEvent = (_dataType: OptionItem, eventData: EventData) => {
    removeClick(eventData.id, eventData.title || '');
  };

  return (
    <>
      <ConfirmModal
        onPositive={handleRemove}
        onNegative={resetDeleteModal}
        positive="Remove"
        title="Remove App?"
        open={!!appId}
        maxWidth="xs"
      >
        <Typography mb={2}>{appsBehavior.getDeleteText()}</Typography>
        <Link target="_blank" href={linkTo.groups.removingAccess} rel="noreferrer">
          Learn more
        </Link>
      </ConfirmModal>
      {showSearch && (
        <Stack sx={{ ...searchSortHeaderStyles }}>
          <Search
            setGlobalFilter={handleSearch}
            placeholder="Search name or description"
          />
        </Stack>
      )}
      <CardList
        rows={groupApps}
        onEvent={onRemoveEvent}
        onFetchMore={handleFetchMore}
        isLoading={isFetching}
        error={isError && 'There was an error fetching apps.'}
        EmptyImage={EmptyImage}
        emptyText={emptyText}
        emptyTextHelper={emptyTextHelper}
        actionItems={[OptionItem.Delete]}
      />
    </>
  );
}
